import { AlertCircle } from 'lucide-react';

// Meal Type — a fieldset of real radios styled as chips. Plain <button>s looked identical but
// told assistive tech nothing: no group name, and the selection was conveyed by background
// colour alone with no programmatic state. Native inputs give the group name, the checked state
// and arrow-key navigation for free. Inputs are sr-only (not hidden) so they stay focusable.
//
// Shared by the manual form and the photo/OCR form. `idPrefix` keeps the radio ids unique if both
// ever mount at once; `name` keeps the two radio groups from capturing each other's clicks.
const MEAL_TYPES = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
  // Recipe label only — desserts are never auto-assigned to a weekly-plan slot.
  { key: 'dessert', label: 'Dessert' },
];

export function MealTypeSelect({ value, onChange, error, idPrefix = 'mealType' }) {
  const errorId = `${idPrefix}-error`;

  return (
    <fieldset
      role="radiogroup"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
    >
      {/* text-base pins the size, since the base typography rule in globals.css covers
          label but not legend and a bare legend renders larger. Deliberately NO
          font-medium: that rule's :where(:not(:has(...))) selector means the sibling
          labels end up at normal weight, so 500 here read as noticeably bolder. */}
      <legend className="block text-foreground mb-2 text-base">
        Meal Type *
      </legend>
      {error && (
        <div id={errorId} className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-[#B85A5A]" />
          <span>{error}</span>
        </div>
      )}
      {/* flex-wrap so the fourth chip drops to a second row rather than overflowing
          the ~448px mobile container. */}
      <div className="flex flex-wrap gap-2">
        {MEAL_TYPES.map((option) => {
          // No '|| lunch' fallback: an unset meal type must leave every chip unselected.
          const isActive = String(value || '').toLowerCase() === option.key;
          return (
            <div key={option.key}>
              <input
                type="radio"
                id={`${idPrefix}-${option.key}`}
                name={idPrefix}
                value={option.key}
                checked={isActive}
                onChange={() => onChange(option.key)}
                className="sr-only peer"
              />
              <label
                htmlFor={`${idPrefix}-${option.key}`}
                className={
                  `block cursor-pointer px-4 py-2 rounded-full text-sm border transition-colors ` +
                  // Focus ring uses the accent pink exactly, matching the selected chip.
                  // Known trade-off: #DFA79E is 2.07:1 against the white card, under the
                  // 3:1 WCAG 1.4.11 asks of a focus indicator (9.58:1 in dark mode).
                  // Chosen deliberately for visual consistency — don't "fix" silently.
                  `peer-focus-visible:ring-2 peer-focus-visible:ring-[#DFA79E] ` +
                  (isActive
                    // Fixed dark ink rather than var(--accent-foreground), which is #FFFFFF
                    // in dark mode and only 2.07:1 on the #DFA79E accent. This is 8.08:1
                    // in both themes, since --accent is #DFA79E in light and dark alike.
                    ? 'bg-[var(--accent)] border-[var(--accent)] text-[#2A1A16]'
                    : 'bg-card border-[var(--border)] text-muted-foreground hover:bg-muted')
                }
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
